import React from "react";
import { useDeploymentHistory } from "./useDeploymentHistory";
import { formatTimestampIST, getElapsedTime } from "../../utils/time-service";
import CenteredSpinner from "../../common/CenteredSpinner";

interface ModalProps {
  show: boolean;
  repo: string;
  env: string;
  onClose: () => void;
}

const Modal = ({ show, repo, env, onClose }: ModalProps) => {
  const {
    deploymentsLog,
    loading,
    page,
    pageSize,
    setPage,
    searchQuery,
    setSearchQuery,
    isPrevDisabled,
    isNextDisabled,
  } = useDeploymentHistory(repo, env);

  const handleClose = () => {
    setSearchQuery(""); // Reset search query state
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-slate-50 rounded-lg shadow-lg p-5 w-11/12 max-w-6xl h-4/5 overflow-y-auto relative">
        <div className="flex justify-between items-center mb-4">
          <h2 className="uppercase font-semibold">
            History ( {repo} / {env} )
          </h2>
          <button className="text-2xl" onClick={handleClose}>
            &times;
          </button>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <input
              type="text"
              placeholder="Search by Deployed User Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border rounded p-2 w-full md:w-64 shadow text-[0.9rem]"
            />
            <div className="flex space-x-2 items-center">
              <button
                onClick={() => setPage((prev) => prev - 1)}
                className={`px-4 py-2 bg-transparent text-2xl ${
                  isPrevDisabled
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-blue-500 text-white"
                }`}
                disabled={isPrevDisabled}
              >
                &lsaquo;
              </button>
              <span className="text-sm mt-1">Page {page}</span>
              <button
                onClick={() => setPage((prev) => prev + 1)}
                className={`px-4 py-2 bg-transparent text-2xl ${
                  isNextDisabled
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-blue-500 text-white"
                }`}
                disabled={isNextDisabled}
              >
                &rsaquo;
              </button>
            </div>
          </div>
          <div className="overflow-auto max-h-[400px]">
            <table className="min-w-full table-auto border border-slate-100">
              <thead className="bg-slate-700 text-white sticky top-0 ">
                <tr>
                  <th className="px-6 py-2 text-left text-sm font-medium uppercase">
                    S.No.
                  </th>
                  <th className="px-6 py-2 text-left text-sm font-medium uppercase">
                    Branch
                  </th>
                  <th className="px-6 py-2 text-left text-sm font-medium uppercase">
                    DB Triggered
                  </th>
                  <th className="px-6 py-2 text-left text-sm font-medium uppercase">
                    Deployed By
                  </th>
                  <th className="px-6 py-2 text-left text-xs font-medium uppercase">
                    Timestamp
                  </th>
                </tr>
              </thead>
              {loading ? (
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td colSpan={5}>
                      <CenteredSpinner variant="fullheight" />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody className="bg-white divide-y divide-gray-200">
                  {deploymentsLog.length ? (
                    deploymentsLog.map((item, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {(page - 1) * pageSize + i + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {item.branch}
                        </td>
                        <td className="px-16 py-4 whitespace-nowrap text-sm">
                          {item.db_triggered ? <>&#x2713;</> : <>&#x274C;</>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {item.deployed_by}
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm"
                          title={formatTimestampIST(item.timestamp)}
                        >
                          {getElapsedTime(item.timestamp)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-8">
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
